import { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

export interface DebugApiCall {
  id: string;
  method: string;
  url: string;
  timestamp: Date;
  status?: number;
  statusText?: string;
  requestData?: any;
  responseData?: any;
  error?: any;
  duration?: number;
  headers?: any;
}

class DebugService {
  private requestMap = new Map<string, { startTime: number; config: AxiosRequestConfig }>();

  /**
   * Generate unique ID for tracking requests
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Emit custom event for debug panel
   */
  private emitDebugEvent(type: string, data: DebugApiCall) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(`debug:api:${type}`, { detail: data }));
    }
  }

  /**
   * Add debug interceptors to an axios instance
   */
  addInterceptors(axiosInstance: AxiosInstance, serviceName: string = 'API') {
    // Request interceptor
    axiosInstance.interceptors.request.use(
      (config) => {
        const requestId = this.generateId();

        // Store request info for later
        this.requestMap.set(requestId, {
          startTime: Date.now(),
          config: config
        });

        // Add request ID to config for tracking
        (config as any).__requestId = requestId;

        // Emit request event
        const debugData: DebugApiCall = {
          id: requestId,
          method: config.method?.toUpperCase() || 'GET',
          url: `${config.baseURL || ''}${config.url || ''}`,
          timestamp: new Date(),
          requestData: config.data,
          headers: config.headers
        };

        this.emitDebugEvent('request', debugData);

        return config;
      },
      (error) => {
        console.error(`[${serviceName}] Request Error:`, error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const requestId = (response.config as any).__requestId;
        const requestInfo = this.requestMap.get(requestId);

        const duration = requestInfo ? Date.now() - requestInfo.startTime : 0;

        const debugData: DebugApiCall = {
          id: requestId,
          method: response.config.method?.toUpperCase() || 'GET',
          url: response.config.url || '',
          timestamp: new Date(),
          status: response.status,
          statusText: response.statusText,
          responseData: response.data,
          duration: duration
        };

        this.emitDebugEvent('response', debugData);

        // Clean up
        this.requestMap.delete(requestId);

        return response;
      },
      (error: AxiosError) => {
        const requestId = (error.config as any)?.__requestId;
        const requestInfo = this.requestMap.get(requestId);

        const duration = requestInfo ? Date.now() - requestInfo.startTime : 0;

        const debugData: DebugApiCall = {
          id: requestId,
          method: error.config?.method?.toUpperCase() || 'GET',
          url: error.config?.url || '',
          timestamp: new Date(),
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          error: {
            message: error.message,
            code: error.code,
            response: error.response?.data
          },
          duration: duration
        };

        console.error(`[${serviceName}] Error:`, error.response?.status || 'Network Error', error.config?.url, `(${duration}ms)`);
        this.emitDebugEvent('error', debugData);

        // Clean up
        this.requestMap.delete(requestId);

        return Promise.reject(error);
      }
    );
  }

  /**
   * Enable debug mode
   */
  enableDebugMode() {
    localStorage.setItem('debugMode', 'true');
  }

  /**
   * Disable debug mode
   */
  disableDebugMode() {
    localStorage.removeItem('debugMode');
  }

  /**
   * Check if debug mode is enabled
   */
  isDebugModeEnabled(): boolean {
    return localStorage.getItem('debugMode') === 'true';
  }
}

// Export singleton instance
const debugService = new DebugService();
export default debugService;